import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Calendar as CalendarIcon, CalendarDays } from 'lucide-react'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Label } from './ui/label'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Calendar } from './ui/calendar'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import {
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
} from '@/redux/service/holiday'
import toast from 'react-hot-toast'
import type { Holiday } from '@/redux/service/holiday'
import { useState } from 'react'

interface HolidayFormValues {
  name: string
  date: string
  description: string
}

interface HolidayModalProps {
  data?: Holiday | null
  isOpen: boolean
  isClose: () => void
}

const HolidayModal: React.FC<HolidayModalProps> = ({
  data,
  isOpen,
  isClose,
}) => {
  const [createHoliday] = useCreateHolidayMutation()
  const [updateHoliday] = useUpdateHolidayMutation()
  const [open, setOpen] = useState(false)

  const isEditMode = Boolean(data?.id)

  const formik = useFormik<HolidayFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: data?.name ?? '',
      date: data?.date ? (data.date.split('T')[0] ?? '') : '',
      description: data?.description ?? '',
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required('Holiday name is required'),
      date: Yup.string().required('Date is required'),
      description: Yup.string().trim(),
    }),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        if (isEditMode && data) {
          await updateHoliday({
            ...values,
            id: data.id,
          }).unwrap()
          toast.success('Holiday updated successfully')
        } else {
          await createHoliday(values).unwrap()
          toast.success('Holiday created successfully')
        }

        formik.resetForm()
        isClose()
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Something went wrong'

        toast.error(message)

        if (message.toLowerCase().includes('date')) {
          setFieldError('date', message)
        } else {
          setFieldError('name', message)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const dateValue = formik.values.date
  const selectedDate =
    dateValue && !Number.isNaN(new Date(dateValue).getTime())
      ? new Date(dateValue)
      : undefined

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          formik.resetForm()
          isClose()
        }
      }}
    >
      <DialogContent className='max-w-[80vw] md:max-w-lg p-0 bg-background border-none shadow-2xl rounded-[2rem] overflow-hidden'>
        <DialogHeader className='p-8 pb-6 bg-gradient-to-br from-primary/5 via-transparent to-transparent border-b border-border/50 relative overflow-hidden'>
          <div className='absolute top-0 right-0 p-8 opacity-[0.03] rotate-12 pointer-events-none'>
            <CalendarDays className='w-32 h-32' />
          </div>
          <DialogTitle className='text-2xl font-black flex items-center gap-4 relative z-10'>
            <div className='p-3 bg-primary/10 rounded-2xl text-primary shadow-sm shadow-primary/10'>
              <CalendarDays className='w-6 h-6' />
            </div>
            <div className='flex flex-col gap-0.5'>
              <span className='tracking-tight'>
                {isEditMode ? 'Edit Holiday' : 'New Holiday'}
              </span>
              <span className='text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]'>
                Calendar Management
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className='p-8 pt-6 space-y-6'>
          {/* HOLIDAY NAME */}
          <div className='space-y-2.5'>
            <Label
              htmlFor='holiday-name'
              className='text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1'
            >
              Holiday Name
            </Label>
            <span className='text-red-500'>*</span>{' '}
            <Input
              id='holiday-name'
              name='name'
              placeholder='e.g. Independence Day'
              value={formik.values.name}
              onChange={formik.handleChange}
              className='h-12 px-4 rounded-2xl bg-muted/30 border-border/50 focus:bg-background transition-all font-medium'
            />
            {formik.touched.name && formik.errors.name && (
              <p className='text-[11px] font-bold text-red-500 ml-1 mt-1 animate-in fade-in slide-in-from-top-1'>
                {formik.errors.name}
              </p>
            )}
          </div>

          {/* DATE — Popover + Calendar picker */}
          <div className='space-y-2.5'>
            <Label className='text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1'>
              Event Date
            </Label>
            <span className='text-red-500'>*</span>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className={cn(
                    'w-full h-12 justify-start text-left font-medium px-4 rounded-2xl bg-muted/30 border-border/50 hover:bg-muted/50 transition-all',
                    !dateValue && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className='mr-3 h-4 w-4 text-primary opacity-60' />
                  {selectedDate ? (
                    format(selectedDate, 'PPP')
                  ) : (
                    <span>Select date...</span>
                  )}
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className='w-auto p-0 border-none shadow-2xl rounded-3xl overflow-hidden'
                align='start'
                sideOffset={8}
              >
                <Calendar
                  mode='single'
                  captionLayout='dropdown'
                  numberOfMonths={1}
                  fromYear={2018}
                  toYear={2035}
                  selected={selectedDate}
                  onSelect={date => {
                    formik.setFieldValue(
                      'date',
                      date ? format(date, 'yyyy-MM-dd') : ''
                    )

                    if (date) {
                      setOpen(false) // Close popover after selecting a date
                    }
                  }}
                  className='p-3'
                />
              </PopoverContent>
            </Popover>
            {formik.touched.date && formik.errors.date && (
              <p className='text-[11px] font-bold text-red-500 ml-1 mt-1 animate-in fade-in slide-in-from-top-1'>
                {formik.errors.date}
              </p>
            )}
          </div>

          {/* DESCRIPTION */}
          <div className='space-y-2.5'>
            <Label
              htmlFor='holiday-description'
              className='text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1'
            >
              Context{' '}
              <span className='lowercase font-bold opacity-60'>(optional)</span>
            </Label>
            <textarea
              id='holiday-description'
              name='description'
              rows={3}
              placeholder='Brief details about this holiday...'
              value={formik.values.description}
              onChange={formik.handleChange}
              className='flex w-full rounded-2xl border border-border/50 bg-muted/30 px-4 py-3 text-sm font-medium transition-all focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/60 resize-none min-h-[100px]'
            />
          </div>

          {/* ACTIONS */}
          <div className='flex items-center gap-3 pt-6 border-t border-border/50'>
            <Button
              type='button'
              variant='ghost'
              className='flex-1 h-12 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-muted transition-all'
              onClick={() => {
                formik.resetForm()
                isClose()
              }}
            >
              Cancel
            </Button>

            <Button
              type='submit'
              disabled={formik.isSubmitting}
              className='flex-[2] h-12 rounded-2xl font-bold text-xs uppercase tracking-widest bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:opacity-90 transition-all'
            >
              {formik.isSubmitting
                ? 'Processing...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Add to Calendar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default HolidayModal
